import React from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';

function TermsPage() {
  const s = "mb-8";
  const h2 = "text-xl font-bold text-white font-rubik mb-3";
  const h3 = "text-lg font-semibold text-white font-rubik mb-2 mt-5";
  const p = "text-gray-300 font-rubik leading-relaxed mb-3 text-sm";
  const ul = "list-disc pl-6 text-gray-300 font-rubik text-sm space-y-1.5 mb-3";

  return (
    <div className="min-h-screen bg-gray-900">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" alt="Civote" className="h-10 w-10 rounded-lg" />
              <span className="text-2xl font-bold text-white font-rubik">CIVOTE</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white font-medium font-rubik transition-colors">
              <MdArrowBack className="w-5 h-5" /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-8 sm:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-rubik mb-2">Terms &amp; Conditions</h1>
            <p className="text-gray-400 font-rubik text-sm mb-8">Effective Date: March 20, 2026 &nbsp;|&nbsp; Last Updated: March 20, 2026</p>

            <div className="max-w-none">
              <section className={s}>
                <p className={p}>
                  These Terms &amp; Conditions ("<strong className="text-white">Terms</strong>") constitute a legally binding agreement between you ("<strong className="text-white">User</strong>," "<strong className="text-white">you</strong>," or "<strong className="text-white">your</strong>") and Civote LLC ("<strong className="text-white">Civote</strong>," "<strong className="text-white">we</strong>," "<strong className="text-white">us</strong>," or "<strong className="text-white">our</strong>") governing your access to and use of the Civote platform, including the website and any associated applications (the "<strong className="text-white">Service</strong>").
                </p>
                <p className={p}>
                  By creating an account or using the Service, you agree to be bound by these Terms and our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. If you do not agree, do not use the Service.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>1. Eligibility</h2>
                <p className={p}>You must be at least 13 years of age to use the Service. By using the Service, you represent and warrant that you meet this age requirement. If you are under 18, you represent that you have the consent of a parent or legal guardian.</p>
              </section>

              <section className={s}>
                <h2 className={h2}>2. Account Registration &amp; Security</h2>
                <ul className={ul}>
                  <li>You must provide accurate and complete information when creating an account.</li>
                  <li>You are solely responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</li>
                  <li>You agree to notify us immediately if you suspect unauthorized use of your account.</li>
                  <li>We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably believe are fraudulent.</li>
                  <li>Each individual may maintain only one (1) account. Creating duplicate accounts to manipulate polls, points, or platform features is prohibited.</li>
                </ul>
              </section>

              <section className={s}>
                <h2 className={h2}>3. Acceptable Use</h2>
                <p className={p}>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree <strong className="text-white">not</strong> to:</p>
                <ul className={ul}>
                  <li>Create polls or submit content that is illegal, harmful, threatening, abusive, harassing, defamatory, obscene, hateful, or discriminatory.</li>
                  <li>Impersonate any person or entity, or falsely claim an affiliation with any person or entity.</li>
                  <li>Use automated scripts, bots, or other means to interact with the Service in an unauthorized manner.</li>
                  <li>Attempt to gain unauthorized access to the Service, other user accounts, or related systems.</li>
                  <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                  <li>Manipulate poll results through coordinated inauthentic behavior, vote brigading, or exploitation of platform mechanics.</li>
                  <li>Upload malicious code, viruses, or any files designed to interrupt, damage, or limit the functionality of any software or hardware.</li>
                </ul>
              </section>

              <section className={s}>
                <h2 className={h2}>4. User-Generated Content</h2>

                <h3 className={h3}>4.1 Custom Polls</h3>
                <p className={p}>
                  Users may create custom polls subject to community guidelines and AI-assisted moderation. All custom polls are subject to administrative review. We reserve the right to approve, reject, flag, or close any user-created poll at our sole discretion.
                </p>

                <h3 className={h3}>4.2 Content Ownership</h3>
                <p className={p}>
                  You retain ownership of the content you submit. By submitting content to the Service, you grant Civote a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute that content in connection with operating and promoting the Service.
                </p>

                <h3 className={h3}>4.3 Content Moderation</h3>
                <p className={p}>
                  Civote uses a combination of automated AI review and human moderation to enforce content guidelines. Polls that are rejected or flagged may result in notification to the creator with a stated reason. Repeated violations may result in account suspension.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>5. Polls, Voting &amp; Data</h2>
                <ul className={ul}>
                  <li><strong className="text-white">Anonymous Responses (Voters):</strong> Your individual poll responses are not publicly associated with your identity. Results are displayed in aggregate only. No other user can view how you voted on any specific poll.</li>
                  <li><strong className="text-white">Public Responses (Representatives):</strong> If you hold an active representative subscription, your poll responses are publicly visible on your representative profile. This is an intentional and core feature of the Service, designed to allow voters to compare their views with those of candidates. By subscribing as a representative, you consent to this public disclosure.</li>
                  <li><strong className="text-white">Demographic Data:</strong> If you provide optional demographic information (gender, ethnicity, education, military service), it may be used in anonymous, aggregated demographic breakdowns of poll results.</li>
                  <li><strong className="text-white">Candidacy Assessments:</strong> Reports generated by the Service are based on aggregated, point-in-time user data and do not constitute a scientific poll, official survey, or certified political analysis. Results are for informational purposes only.</li>
                  <li><strong className="text-white">No Guarantee of Accuracy:</strong> We do not warrant that poll results or candidacy assessments are representative of any specific population or electorate.</li>
                </ul>
              </section>

              <section className={s}>
                <h2 className={h2}>6. Gamification &amp; Points</h2>
                <ul className={ul}>
                  <li>Poll Points, badges, streaks, and rating tiers are virtual, non-transferable rewards with no monetary value.</li>
                  <li>Points may be used within the platform to create custom polls or access certain features.</li>
                  <li>We reserve the right to modify, suspend, or discontinue the gamification system at any time.</li>
                  <li>Points cannot be exchanged for cash, transferred to other users, or redeemed outside the Service.</li>
                </ul>
              </section>

              <section className={s}>
                <h2 className={h2}>7. Representative Profiles &amp; Subscriptions</h2>

                <h3 className={h3}>7.1 Public Profile Disclosure</h3>
                <p className={p}>
                  <strong className="text-white">Representative profiles are visible to the public.</strong> By subscribing as a representative, you acknowledge and agree that your profile information (name, photo, biography, office sought, campaign website, geographic information) and your poll responses will be publicly displayed on the Service. This public visibility enables voters to evaluate candidates and generate candidacy assessment reports comparing their views to yours.
                </p>
                <p className={p}>
                  You are solely responsible for the accuracy of the information on your representative profile. Misrepresenting your identity, office sought, or political positions may result in profile revocation.
                </p>

                <h3 className={h3}>7.2 Subscription Plans</h3>
                <p className={p}>
                  Representative profiles are available through paid subscription plans. Pricing, features, and billing terms are displayed at the time of purchase. Subscriptions automatically renew at the end of each billing period unless cancelled.
                </p>

                <h3 className={h3}>7.3 Payment Processing</h3>
                <p className={p}>
                  All payments are processed securely through Stripe, Inc. By subscribing, you agree to Stripe's terms of service. Civote does not store your credit card information.
                </p>

                <h3 className={h3}>7.4 Cancellation &amp; Refunds</h3>
                <p className={p}>
                  You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. Refunds are generally not provided for partial billing periods unless required by applicable law.
                </p>

                <h3 className={h3}>7.5 Administrative Revocation</h3>
                <p className={p}>
                  We reserve the right to revoke representative profiles that violate these Terms, misrepresent candidacy, or otherwise abuse the platform. Users will be notified of revocation and may contact support for more information.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>8. Intellectual Property</h2>
                <p className={p}>
                  The Service, including its design, text, graphics, logos, icons, software, and all other content (excluding User-Generated Content), is the property of Civote LLC and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any part of the Service without our prior written consent.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>9. Disclaimer of Warranties</h2>
                <p className={p}>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>10. Limitation of Liability</h2>
                <p className={p}>
                  TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, CIVOTE LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p className={p}>
                  IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>11. Indemnification</h2>
                <p className={p}>
                  You agree to indemnify, defend, and hold harmless Civote LLC and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your violation of these Terms, or your violation of any rights of a third party.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>12. Termination</h2>
                <p className={p}>
                  We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service ceases immediately. Sections of these Terms that by their nature should survive termination (including Disclaimers, Limitation of Liability, and Indemnification) shall survive.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>13. Governing Law &amp; Dispute Resolution</h2>
                <p className={p}>
                  These Terms are governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict-of-laws principles. Any disputes arising from these Terms or the Service shall be resolved through binding arbitration administered in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive or equitable relief in a court of competent jurisdiction.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>14. Modifications to the Service &amp; Terms</h2>
                <p className={p}>
                  We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time. We may also update these Terms from time to time. When we make material changes, we will update the "Last Updated" date and, where appropriate, notify you via email or in-app notification. Continued use of the Service after changes are posted constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>15. Severability</h2>
                <p className={p}>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the remaining provisions remain in full force and effect.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>16. Entire Agreement</h2>
                <p className={p}>
                  These Terms, together with the <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, constitute the entire agreement between you and Civote LLC regarding the Service and supersede all prior agreements and understandings.
                </p>
              </section>

              <section className={s}>
                <h2 className={h2}>17. Contact</h2>
                <p className={p}>For questions about these Terms &amp; Conditions, please contact us:</p>
                <div className="bg-gray-700/30 rounded-xl p-5 border border-gray-600/50">
                  <p className="text-white font-rubik font-semibold text-sm">Civote LLC</p>
                  <p className="text-gray-300 font-rubik text-sm mt-1">
                    Email: <a href="mailto:privacy@civote.com" className="text-primary hover:underline">privacy@civote.com</a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-gray-900 border-t border-gray-800 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 font-rubik text-sm">&copy; {new Date().getFullYear()} Civote LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default TermsPage;
